/**
 * The designer's light/dark theme: one stored preference, one attribute, one writer.
 *
 * The theme is `data-theme` on `<html>`; the dark palette in `styles/base/color-palette.scss`
 * matches `:root[data-theme="dark"]`, so flipping it costs one attribute write. The blocking
 * `<head>` scripts in `public/index.html` and `public/login.html` set that attribute from
 * {@link THEME_STORAGE_KEY} before the first paint, and {@link followThemePreference} takes over
 * once the bundle runs — the preference belongs to the browser, not to the account, so there is
 * nothing to wait for before painting.
 *
 * What the user picks ({@link ThemePreference}) is not always what gets painted
 * ({@link DesignerTheme}): "system" hands the choice to the OS colour scheme and keeps following
 * it. Dark is the default. The only stored values that change it are an explicit `"light"` and
 * `"system"`, so a missing key, a blocked `localStorage` and an unrecognised value all land on dark.
 */

export type DesignerTheme = "light" | "dark";

/** What the user picked in Appearance; `"system"` paints whatever the OS colour scheme is. */
export type ThemePreference = "system" | DesignerTheme;

/**
 * Keep in step with the pre-paint scripts in `public/index.html` and `public/login.html`,
 * which cannot import it. `designer-theme.test.ts` runs both scripts against this key.
 */
export const THEME_STORAGE_KEY: string = "kuika-designer-theme";

export const DEFAULT_THEME: DesignerTheme = "dark";

/** The OS colour-scheme query the "system" preference follows; the pre-paint scripts spell it out too. */
export const SYSTEM_DARK_QUERY: string = "(prefers-color-scheme: dark)";

const isThemePreference = (value: string | null): value is ThemePreference => {
  return value === "system" || value === "light" || value === "dark";
};

/** What the user saved, or {@link DEFAULT_THEME} when they have not saved anything readable. */
export const readThemePreference = (): ThemePreference => {
  try {
    const stored: string | null = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : DEFAULT_THEME;
  } catch {
    // Site data blocked.
    return DEFAULT_THEME;
  }
};

/** `null` where the OS setting cannot be read (no `matchMedia`, or it throws). */
const querySystemDark = (): MediaQueryList | null => {
  try {
    return typeof window.matchMedia === "function" ? window.matchMedia(SYSTEM_DARK_QUERY) : null;
  } catch {
    return null;
  }
};

/** The theme a preference paints right now. An unreadable OS setting lands on {@link DEFAULT_THEME}. */
export const resolveTheme = (preference: ThemePreference): DesignerTheme => {
  if (preference !== "system") {
    return preference;
  }

  const systemDark: MediaQueryList | null = querySystemDark();
  if (systemDark === null) {
    return DEFAULT_THEME;
  }

  return systemDark.matches ? "dark" : "light";
};

/**
 * The theme actually painted. Anything that picks a theme-specific asset at render time reads
 * this rather than the stored preference, so an asset can never disagree with the stylesheet.
 * It mirrors the stylesheet exactly: only `data-theme="dark"` is dark.
 */
export const getAppliedTheme = (): DesignerTheme => {
  return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
};

/** Paints `theme` without saving it. */
export const applyTheme = (theme: DesignerTheme): void => {
  document.documentElement.setAttribute("data-theme", theme);
};

/**
 * Calls `listener` with the theme the OS colour scheme asks for each time it changes. Returns the
 * unsubscribe; a no-op where the OS setting cannot be read. Safari before 14 only has the
 * deprecated `addListener` pair, as in `useMediaQuery`.
 */
export const watchSystemTheme = (listener: (theme: DesignerTheme) => void): (() => void) => {
  const systemDark: MediaQueryList | null = querySystemDark();
  if (systemDark === null) {
    return (): void => {};
  }

  const handleChange = (event: MediaQueryListEvent | MediaQueryList): void => {
    listener(event.matches ? "dark" : "light");
  };

  if (typeof systemDark.addEventListener === "function") {
    systemDark.addEventListener("change", handleChange);
    return (): void => systemDark.removeEventListener("change", handleChange);
  }

  systemDark.addListener(handleChange);
  return (): void => systemDark.removeListener(handleChange);
};

/** The live OS subscription while the preference is "system"; one per page. */
let stopFollowingSystemTheme: (() => void) | null = null;

/**
 * An OS flip repaints through {@link applyTheme}, not a bare attribute write, so mounted component
 * icons follow it too. It is not animated: the user did not ask for this change here.
 */
const followSystemTheme = (isFollowing: boolean): void => {
  if (isFollowing && stopFollowingSystemTheme !== null) {
    return;
  }

  stopFollowingSystemTheme?.();
  stopFollowingSystemTheme = isFollowing ? watchSystemTheme(applyTheme) : null;
};

/** Saves `preference` as this browser's choice, paints it, and follows the OS while it is "system". */
export const setThemePreference = (preference: ThemePreference): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Site data blocked; the choice lasts until the page reloads.
  }
  applyTheme(resolveTheme(preference));
  followSystemTheme(preference === "system");
};

/**
 * Called once from each entry point. The pre-paint script has already painted the saved preference;
 * this keeps a "system" preference following the OS for the life of the page.
 */
export const followThemePreference = (): void => {
  followSystemTheme(readThemePreference() === "system");
};

/**
 * Calls `listener` with the painted theme each time it changes, whoever changed it — the
 * Appearance menu, the UI Kit Lab, or a bare attribute write. For anything that cannot follow
 * the attribute through CSS: values resolved into SVG attributes, canvas pixels, third-party
 * widgets that take a theme name. Returns the unsubscribe.
 */
export const watchAppliedTheme = (listener: (theme: DesignerTheme) => void): (() => void) => {
  let current: DesignerTheme = getAppliedTheme();
  const observer: MutationObserver = new MutationObserver((): void => {
    const next: DesignerTheme = getAppliedTheme();
    if (next === current) return;
    current = next;
    listener(next);
  });
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  return (): void => observer.disconnect();
};
